import { z } from "zod";
import { uuidLoose } from "./uuid.js";
/**
 * Wire-format DTOs for the P16 quote workflow.
 *
 * Buyer-initiated quote → admin revises items + prices → customer accepts →
 * acceptance tx creates a `placed` order routed through P15 paymentTerms.
 *
 * Enum values mirror `packages/db/src/schema/quotes.ts` (source of truth):
 *   - `quoteStatusValues` (6 statuses)
 *   - `quotePaymentTermsOverrideValues` (3 overrides; `null` means "use the
 *     company default" — modelled as the schema's nullable column)
 *
 * Money shape: every `_minor` field is an integer count of the smallest
 * currency unit (pence for GBP). Mirrors `bigint mode:"number"` columns in
 * `quote_items`, matching the repo-wide money convention (see `money.ts`
 * + `orders.totals_*_minor` + `invoices.totalMinor`).
 *
 * VAT snapshot: `vat_category_code` + `vat_rate_basis_points` are frozen at
 * quote-submit time so a mid-quote rate change in `vat_categories.rate_basis_points`
 * cannot move the price under the buyer (spec §7). 2000 basis points = 20.00%.
 * This snapshot pattern mirrors `order_items.{vatCategoryCodeSnapshot,
 * vatRateBasisPointsSnapshot}` — the `_snapshot` suffix is omitted on quote
 * items because the row itself is inherently a request-time snapshot.
 */
// ---------- Enums (mirrored from packages/db/src/schema/quotes.ts) ----------
export const QuoteStatusSchema = z.enum([
    "requested",
    "revised",
    "accepted",
    "rejected",
    "cancelled",
    "expired",
]);
/**
 * Per-quote payment-terms override. `null` means "fall back to
 * companies.paymentTerms" — at accept time the API computes
 * `effective_payment_terms = quote.payment_terms_override ?? company.paymentTerms`.
 *
 * P17 adds `cod` — admin may override a quote to Cash on Delivery; gated at
 * accept time by `companies.cod_enabled` (see `/errors/quote.cod_not_eligible`).
 */
export const QuotePaymentTermsOverrideSchema = z
    .enum(["immediate", "net_30", "net_60", "cod"])
    .nullable();
/**
 * Resolved (post-override-collapse) payment terms used by the acceptance flow
 * to pick between Stripe PI (`immediate`), InvoiceService.issueForOrder
 * (`net_30` / `net_60`), or the COD branch (`cod`, P17 — order placed without
 * payment, invoice issued at mark-collected). Never null — the company always
 * has a default.
 */
export const EffectivePaymentTermsSchema = z.enum([
    "immediate",
    "net_30",
    "net_60",
    "cod",
]);
// ---------- Request bodies ----------
export const SubmitQuoteItemSchema = z.object({
    variant_id: uuidLoose,
    /** Client-side validated against P14a MOQ + order-increment; server re-asserts. */
    quantity: z.number().int().positive(),
});
/**
 * POST /v1/storefront/quotes — customer submits a cart as a quote request.
 * Enforces 1..200 items (cart guardrail mirrors the checkout cap).
 *
 * UUIDs use `uuidLoose` per the repo convention for storefront mutation
 * bodies (see `uuid.ts`) — Zod v4's strict `.uuid()` rejects seeded
 * version-0 ids used in P02-T12 dev/staging fixtures.
 */
export const SubmitQuoteBodySchema = z.object({
    company_location_id: uuidLoose,
    requester_note: z.string().max(2000).optional(),
    items: z.array(SubmitQuoteItemSchema).min(1).max(200),
});
export const ReviseQuoteItemSchema = z.object({
    variant_id: uuidLoose,
    quantity: z.number().int().positive(),
    /**
     * `null` (or omitted) keeps the request-time `requested_unit_price_minor`
     * as the effective unit price. Otherwise the staff-quoted price wins at
     * accept time (per `effective = quoted ?? requested`).
     */
    quoted_unit_price_minor: z.number().int().nonnegative().nullable().optional(),
});
/**
 * PATCH /v1/admin/quotes/:id — admin revises items + prices + terms.
 * `valid_until` is required at revise (the customer needs a deadline to
 * accept; submit-time quotes are open-ended until the admin commits).
 */
export const ReviseQuoteBodySchema = z.object({
    /** ISO-8601 UTC datetime. */
    valid_until: z.string().datetime(),
    payment_terms_override: QuotePaymentTermsOverrideSchema.optional(),
    admin_note: z.string().max(2000).optional(),
    items: z.array(ReviseQuoteItemSchema).min(1).max(200),
});
// ---------- Soft warnings (non-throwing limit checks at submit time) ----------
/**
 * Soft warnings surfaced at quote-submit time by
 * `financial-limits.computeQuoteWarnings`. These do NOT block submission
 * (the buyer can still ask); at accept time the corresponding
 * `assertCartUnderSpendingLimit` / `assertCartUnderCreditLimit` /
 * `assertNoOverdueInvoices` will throw if the limit is still exceeded.
 */
export const QuoteWarningSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("spending_limit_exceeded"),
        total_minor: z.number().int(),
        limit_minor: z.number().int(),
    }),
    z.object({
        kind: z.literal("credit_limit_exceeded"),
        total_minor: z.number().int(),
        available_minor: z.number().int(),
    }),
]);
